import React from 'react';
import { View } from 'react-native';

import { Text } from '../../components';
import { SecondaryButton, Tag } from '../../design/components';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import { isMyDayTraining } from './myDayTraining';

// Compact training strip (one line plus the actions) so it never crowds the dock.
// The full explanation stays available to screen readers on the text line.
const TrainingModeBanner = ({
	trainingContext,
	isOnline = true,
	onBack,
	onChooseEmployee,
	backLabel = 'Back to employee preview',
}) => {
	const styles = useThemedStyles(createStyles);
	if (!isMyDayTraining(trainingContext)) {
		return null;
	}

	const driverName = trainingContext.targetDriverName;
	const explanation = isOnline
		? `Training mode. Working with ${driverName}'s run. Actions are recorded under your admin account. ${driverName} remains assigned to the run and must complete personal driver actions.`
		: 'Training mode. Reconnect to continue training. Training actions are never saved to the offline queue.';

	return (
		<View style={styles.banner} accessibilityRole="alert">
			<View style={styles.inner}>
				<View style={styles.titleRow} accessible={true} accessibilityLabel={explanation}>
					<Tag label="Training mode" tone="warning" size="regular" />
					{/* Two lines so long names are not cut at phone width. */}
					<Text style={[styles.title, !isOnline && styles.titleOffline]} numberOfLines={2}>
						{isOnline ? `${driverName}'s run · logged as you` : 'Offline: reconnect to continue'}
					</Text>
				</View>
				{Boolean(onBack || onChooseEmployee) && (
					<View style={styles.actions}>
						{Boolean(onBack) && (
							<SecondaryButton
								label="Back to preview"
								accessibilityLabel={backLabel}
								onPress={onBack}
								style={styles.action}
							/>
						)}
						{Boolean(onChooseEmployee) && (
							<SecondaryButton label="Choose employee" onPress={onChooseEmployee} style={styles.action} />
						)}
					</View>
				)}
			</View>
		</View>
	);
};

const createStyles = ({ ui: { colors, type, space, layout } }) => ({
	banner: {
		backgroundColor: colors.warningBg,
		borderTopWidth: 1,
		borderBottomWidth: 1,
		borderColor: colors.warningBorder,
		paddingHorizontal: space.gutter,
		paddingVertical: space.sm,
	},
	inner: {
		width: '100%',
		maxWidth: layout.maxContentWidth,
		alignSelf: 'center',
		gap: 6,
	},
	titleRow: {
		flexDirection: 'row',
		alignItems: 'center',
		gap: space.sm,
	},
	title: {
		...type.bodySmallSemibold,
		color: colors.text,
		flexShrink: 1,
	},
	titleOffline: {
		color: colors.warning,
	},
	actions: {
		flexDirection: 'row',
		gap: space.sm,
	},
	action: {
		flex: 1,
		paddingHorizontal: space.sm,
	},
});

export default TrainingModeBanner;
